import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import './AdminDashboard.css';
import './AdminForms.css';

const emptySlotForm = {
  code: '',
  label: '',
  sessionType: 'morning',
  sortOrder: 0,
};

const AdminTimeSlots = () => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptySlotForm);

  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('adminToken')
      : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const loadSlots = () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios
      .get(`${API_BASE}/api/admin/time-slots`, {
        headers: authHeaders,
      })
      .then((res) => {
        setTimeSlots(res.data.timeSlots || []);
      })
      .catch(() => {
        setTimeSlots([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptySlotForm);
  };

  const startEdit = (slot) => {
    setEditingId(slot.id);
    setForm({
      code: slot.code || '',
      label: slot.label || '',
      sessionType: slot.session_type || 'morning',
      sortOrder: slot.sort_order ?? 0,
    });
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      // eslint-disable-next-line no-alert
      alert('يرجى تسجيل الدخول مرة أخرى');
      return;
    }

    setSaving(true);
    const payload = {
      code: form.code.trim(),
      label: form.label.trim(),
      sessionType: form.sessionType,
      sortOrder:
        form.sortOrder !== '' ? Number(form.sortOrder) || 0 : 0,
    };

    try {
      if (editingId) {
        await axios.put(
          `${API_BASE}/api/admin/time-slots/${editingId}`,
          payload,
          { headers: authHeaders }
        );
      } else {
        await axios.post(
          `${API_BASE}/api/admin/time-slots`,
          payload,
          { headers: authHeaders }
        );
      }
      setForm(emptySlotForm);
      setEditingId(null);
      loadSlots();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.error || 'تعذر حفظ الوقت');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slot) => {
    if (!token) {
      // eslint-disable-next-line no-alert
      alert('يرجى تسجيل الدخول مرة أخرى');
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `هل أنت متأكد من حذف الوقت "${slot.label}"؟`
    );
    if (!ok) return;

    try {
      await axios.delete(
        `${API_BASE}/api/admin/time-slots/${slot.id}`,
        { headers: authHeaders }
      );
      if (editingId === slot.id) {
        setEditingId(null);
        setForm(emptySlotForm);
      }
      loadSlots();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.error || 'تعذر حذف الوقت');
    }
  };

  return (
    <div className="admin-dashboard">
      <h1 className="admin-page-title">جدول الأوقات</h1>

      <div className="admin-page-content">
        <div className="admin-page-main">
          <div className="admin-list-header">
            <h2>الأوقات المعرفة</h2>
            <button
              type="button"
              className="btn-primary"
              onClick={startCreate}
            >
              وقت جديد
            </button>
          </div>

          {loading ? (
            <div className="admin-loading">جاري التحميل...</div>
          ) : timeSlots.length === 0 ? (
            <div className="admin-empty">لا توجد أوقات معرفة بعد.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>الكود</th>
                    <th>الوقت</th>
                    <th>الفترة</th>
                    <th>ترتيب</th>
                    <th>التحكم</th>
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map((slot) => (
                    <tr key={slot.id}>
                      <td>{slot.code}</td>
                      <td>{slot.label}</td>
                      <td>
                        {slot.session_type === 'evening'
                          ? 'مسائي'
                          : 'صباحي'}
                      </td>
                      <td>{slot.sort_order ?? 0}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => startEdit(slot)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className="btn-link danger"
                          onClick={() => handleDelete(slot)}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-page-side">
          <h2>{editingId ? 'تعديل وقت' : 'إضافة وقت جديد'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>الكود</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                required
                placeholder="مثال: m1 أو e1"
              />
            </div>
            <div className="form-group">
              <label>نص الوقت (كما يظهر للمستخدم)</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => handleChange('label', e.target.value)}
                required
                placeholder="مثال: 08:00 - 09:00"
              />
            </div>
            <div className="form-row inline-row">
              <div className="form-group">
                <label>الفترة</label>
                <select
                  value={form.sessionType}
                  onChange={(e) =>
                    handleChange('sessionType', e.target.value)
                  }
                >
                  <option value="morning">صباحي</option>
                  <option value="evening">مسائي</option>
                </select>
              </div>
              <div className="form-group">
                <label>ترتيب العرض</label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    handleChange('sortOrder', e.target.value)
                  }
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? 'جارٍ الحفظ...' : 'حفظ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminTimeSlots;

